import { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { useProjects } from "@/context/ProjectContext";
import { useAuth, isWriter, isAdmin } from "@/context/AuthContext";
import { PageHeader, SourceLink, EmptyState } from "@/components/primitives";
import { Modal, Field, inputCls, PrimaryButton, GhostButton } from "@/components/Modal";
import { StatusBadge } from "@/components/StatusBadge";
import { AudienceSelector } from "@/components/AudienceSelector";
import { toast } from "sonner";
import { Plus, Trash } from "@phosphor-icons/react";

const STATUSES = ["Not confirmed", "In progress", "Complete"];

export default function Production() {
  const { currentId } = useProjects();
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({});
  const [creating, setCreating] = useState(false);
  const [newItem, setNewItem] = useState({ name: "", description: "" });

  const load = useCallback(async () => {
    if (!currentId) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/api/projects/${currentId}/production`);
      setItems(data);
    } finally { setLoading(false); }
  }, [currentId]);

  useEffect(() => { load(); }, [load]);

  const openEdit = (it) => {
    setForm({ status: it.status, owner: it.owner || "", notes: it.notes || "", evidence: (it.evidence_links || []).join("\n"), audiences: it.audiences || ["internal"] });
    setEditing(it);
  };
  const save = async () => {
    try {
      await api.put(`/api/production/${editing.id}`, {
        status: form.status, owner: form.owner || null, notes: form.notes,
        evidence_links: form.evidence.split("\n").map((s) => s.trim()).filter(Boolean),
        audiences: form.audiences,
      });
      toast.success("Workstream updated"); setEditing(null); load();
    } catch { toast.error("Update failed"); }
  };
  const create = async () => {
    if (!newItem.name.trim()) { toast.error("Name required"); return; }
    try { await api.post(`/api/projects/${currentId}/production`, newItem); toast.success("Added"); setCreating(false); setNewItem({ name: "", description: "" }); load(); }
    catch { toast.error("Create failed"); }
  };
  const remove = async (it) => {
    if (!window.confirm("Delete workstream?")) return;
    try { await api.delete(`/api/production/${it.id}`); toast.success("Deleted"); load(); } catch { toast.error("Delete failed"); }
  };

  if (!currentId || loading) return <div className="cm-label animate-pulse">Loading…</div>;

  return (
    <div data-testid="production-page">
      <PageHeader
        eyebrow="Production & Post"
        title="Workstreams & Milestones"
        description="Editing, sound, color/DI, deliverables, score and titles. Statuses start as 'Not confirmed' — no deadlines, personnel or progress are invented."
      >
        {isWriter(user) && <PrimaryButton data-testid="production-add-btn" onClick={() => setCreating(true)}><span className="inline-flex items-center gap-1"><Plus size={14} /> Add workstream</span></PrimaryButton>}
      </PageHeader>

      {items.length === 0 ? <EmptyState title="No workstreams" /> : (
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          {items.map((it) => (
            <div key={it.id} className="border border-[#2C2C2A] bg-[#1A1A18] p-6" data-testid={`production-card-${it.order}`}>
              <div className="flex items-start justify-between gap-3">
                <div>
                  <h3 className="font-display text-xl">{it.name}</h3>
                  <p className="mt-1 text-sm text-[#A1A19D]">{it.description}</p>
                </div>
                <StatusBadge status={it.status} data-testid={`production-status-${it.order}`} />
              </div>
              <div className="mt-4 border-t border-[#2C2C2A] pt-3 text-sm">
                <div className="cm-label">Owner</div>
                <div className="text-[#F9F9F8]">{it.owner || "Not confirmed"}</div>
                {it.notes && <p className="mt-2 text-sm text-[#A1A19D]">{it.notes}</p>}
                {it.evidence_links?.length > 0 && (
                  <div className="mt-2 space-y-1">{it.evidence_links.map((l, i) => <SourceLink key={i} url={l} label={`Evidence ${i + 1}`} />)}</div>
                )}
              </div>
              <div className="mt-4 flex items-center gap-3">
                {isWriter(user) && <button onClick={() => openEdit(it)} className="text-xs text-[#F59E0B] hover:underline" data-testid={`production-edit-${it.order}`}>Edit</button>}
                {isAdmin(user) && <button onClick={() => remove(it)} className="text-[#A1A19D] hover:text-[#e07a7a]"><Trash size={14} /></button>}
              </div>
            </div>
          ))}
        </div>
      )}

      <Modal open={!!editing} onClose={() => setEditing(null)} title={`Edit · ${editing?.name}`} testid="production-edit-modal">
        <Field label="Status">
          <select className={inputCls} data-testid="production-status-select" value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })}>
            {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        </Field>
        <Field label="Owner"><input className={inputCls} value={form.owner} onChange={(e) => setForm({ ...form, owner: e.target.value })} placeholder="Not confirmed" /></Field>
        <Field label="Notes"><textarea className={inputCls} rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></Field>
        <Field label="Evidence links (one per line)"><textarea className={inputCls} rows={2} value={form.evidence} onChange={(e) => setForm({ ...form, evidence: e.target.value })} /></Field>
        <div className="mb-4"><AudienceSelector value={form.audiences} onChange={(a) => setForm({ ...form, audiences: a })} /></div>
        <div className="flex justify-end gap-2">
          <GhostButton onClick={() => setEditing(null)}>Cancel</GhostButton>
          <PrimaryButton data-testid="production-save" onClick={save}>Save</PrimaryButton>
        </div>
      </Modal>

      <Modal open={creating} onClose={() => setCreating(false)} title="Add workstream" testid="production-create-modal">
        <Field label="Name"><input className={inputCls} data-testid="production-new-name" value={newItem.name} onChange={(e) => setNewItem({ ...newItem, name: e.target.value })} /></Field>
        <Field label="Description"><input className={inputCls} value={newItem.description} onChange={(e) => setNewItem({ ...newItem, description: e.target.value })} /></Field>
        <div className="flex justify-end gap-2">
          <GhostButton onClick={() => setCreating(false)}>Cancel</GhostButton>
          <PrimaryButton data-testid="production-create-save" onClick={create}>Add</PrimaryButton>
        </div>
      </Modal>
    </div>
  );
}
